import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../../store/authStore';
import { PlusCircle, Download, FileSpreadsheet, Eye, X } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../../services/api';

const API_URL = process.env.REACT_APP_API_URL || `${API_BASE_URL}/api`;

const CampaignDashboard = () => {
  const { token } = useAuthStore();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    
    // Auto-refresh the campaigns list every 5 seconds to show live progress
    const interval = setInterval(() => {
      fetchCampaigns();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/whatsapp/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.data);
    } catch (error) {
      console.error('Error fetching campaigns', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'processing': return 'text-blue-600 bg-blue-50';
      case 'delivered': return 'text-emerald-600 bg-emerald-50';
      case 'read': return 'text-indigo-600 bg-indigo-50';
      case 'sent': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleViewCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    setLoadingRecipients(true);
    try {
      const response = await axios.get(`${API_URL}/whatsapp/campaigns/${campaign._id}/recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching recipients', error);
      toast.error('Failed to load campaign details');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleDownloadReport = async (campaign) => {
    try {
      const response = await axios.get(`${API_URL}/whatsapp/campaigns/${campaign._id}/recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const recipientsData = response.data.data || [];
      
      if (recipientsData.length === 0) {
        toast.error("No recipients found for this campaign.");
        return;
      }

      const wsData = recipientsData.map(rec => ({
        "Phone Number": String(rec.phone).startsWith('+') ? rec.phone : `+${rec.phone}`,
        "Status": rec.status,
        "Message ID": rec.messageSid || '-',
        "Error Details": rec.errorReason || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Recipients");
      XLSX.writeFile(wb, `Campaign_${campaign.name}_Report.xlsx`);
      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error('Error downloading report', error);
      toast.error('Failed to download report');
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { country_code: "91", phone: "9876543210", var1: "John", var2: "Diwali Offer", note: "India. Map country_code and phone separately." },
      { country_code: "1", phone: "234567890", var1: "Jane", var2: "New Year Offer", note: "USA. Do not use + or spaces." }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "campaign_template.xlsx");
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bulk Messages</h1>
          <p className="text-slate-500 text-sm mt-1">Manage and track your WhatsApp broadcast campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          {/* <a 
            href="https://business.facebook.com/wa/manage/message-templates/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 font-medium rounded-lg shadow-sm hover:bg-slate-50 transition-colors"
          >
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z"/></svg>
            Manage Templates
          </a> */}
          <button 
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 font-medium rounded-lg shadow-sm hover:bg-slate-50 transition-colors"
          >
            <FileSpreadsheet size={18} className="text-emerald-600" />
            Download Excel Template
          </button>
          <Link 
            to="/whatsapp-campaigns/create" 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition-colors"
          >
            <PlusCircle size={18} />
            Create Campaign
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-slate-700 font-medium border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Campaign Name</th>
                <th className="px-6 py-4">Template</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-center">Total</th>
                <th className="px-6 py-4 text-center">Sent</th>
                <th className="px-6 py-4 text-center">Delivered</th>
                <th className="px-6 py-4 text-center">Read</th>
                <th className="px-6 py-4 text-center">Failed</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-slate-500">Loading campaigns...</td>
                </tr>
              ) : campaigns.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-slate-500">No campaigns found. Create one to get started!</td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-slate-800">{campaign.name}</td>
                    <td className="px-6 py-4">{campaign.templateName}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center font-medium">{campaign.total}</td>
                    <td className="px-6 py-4 text-center text-blue-600 font-medium">{campaign.sent}</td>
                    <td className="px-6 py-4 text-center text-green-600 font-medium">{campaign.delivered}</td>
                    <td className="px-6 py-4 text-center text-indigo-600 font-medium">{campaign.read}</td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={async () => {
                          if (campaign.failed > 0) {
                            try {
                              const res = await axios.get(`${API_URL}/whatsapp/campaigns/${campaign._id}/recipients`, { headers: { Authorization: `Bearer ${token}` } });
                              const failedRec = res.data.data.find(r => r.status === 'failed');
                              if (failedRec) toast.error(`Error: ${failedRec.errorReason}`);
                            } catch(e) { toast.error("Could not fetch error details"); }
                          }
                        }}
                        className={`font-medium ${campaign.failed > 0 ? 'text-red-600 hover:underline cursor-pointer' : 'text-slate-500'}`}
                        title={campaign.failed > 0 ? "Click to view error reason" : ""}
                      >
                        {campaign.failed}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-xs">{new Date(campaign.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => handleViewCampaign(campaign)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" 
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          onClick={() => handleDownloadReport(campaign)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                          title="Download Report"
                        >
                          <Download size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-xl">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Campaign Details: {selectedCampaign.name}</h3>
                <p className="text-sm text-slate-500 mt-0.5">Template: <span className="font-medium">{selectedCampaign.templateName}</span></p>
              </div>
              <button 
                onClick={() => setSelectedCampaign(null)}
                className="p-2 bg-white border border-slate-200 hover:bg-slate-100 hover:text-slate-800 rounded-full text-slate-500 transition-colors shadow-sm"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50">
                  <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Total Sent</div>
                  <div className="text-3xl font-bold text-blue-700">{selectedCampaign.sent}</div>
                </div>
                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/50">
                  <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">Delivered</div>
                  <div className="text-3xl font-bold text-emerald-700">{selectedCampaign.delivered}</div>
                </div>
                <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50">
                  <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-1">Read</div>
                  <div className="text-3xl font-bold text-indigo-700">{selectedCampaign.read}</div>
                </div>
                <div className="bg-red-50/50 p-4 rounded-xl border border-red-100/50">
                  <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Failed</div>
                  <div className="text-3xl font-bold text-red-700">{selectedCampaign.failed}</div>
                </div>
              </div>

              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                Recipient List
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full border border-slate-200">
                  {recipients.length} total
                </span>
              </h4>
              
              {loadingRecipients ? (
                <div className="text-center py-12 text-slate-500">Loading recipient data...</div>
              ) : recipients.length === 0 ? (
                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  No recipients found for this campaign.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-700">
                      <tr>
                        <th className="px-5 py-3.5 font-medium">Phone Number</th>
                        <th className="px-5 py-3.5 font-medium">Status</th>
                        <th className="px-5 py-3.5 font-medium">Message ID</th>
                        <th className="px-5 py-3.5 font-medium">Error Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {recipients.map((rec, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3 font-medium text-slate-700">
                            {String(rec.phone).startsWith('+') ? rec.phone : `+${rec.phone}`}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize tracking-wide ${getStatusColor(rec.status)}`}>
                              {rec.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-400 font-mono">
                            {rec.messageSid || '-'}
                          </td>
                          <td className="px-5 py-3 text-xs text-red-500 max-w-xs truncate" title={rec.errorReason}>
                            {rec.errorReason || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDashboard;
